import { decodeLevelSeries, decodeExtremaSeries, type LevelSeries, type ExtremaSeries, type Extremum } from '@quicktide/shared'
import { db } from '../db'

export interface Point {
  time: number
  level: number
}

const levelCache = new Map<string, LevelSeries | null>()
const extremaCache = new Map<string, ExtremaSeries | null>()

export async function loadLevels(station: string, year: number): Promise<LevelSeries | null> {
  const key = `${station}:${year}:levels`
  if (!levelCache.has(key)) {
    const row = await db.series.get(key)
    levelCache.set(key, row ? decodeLevelSeries(row.bytes) : null)
  }
  return levelCache.get(key) ?? null
}

export async function loadExtrema(station: string, year: number): Promise<ExtremaSeries | null> {
  const key = `${station}:${year}:extrema`
  if (!extremaCache.has(key)) {
    const row = await db.series.get(key)
    extremaCache.set(key, row ? decodeExtremaSeries(row.bytes) : null)
  }
  return extremaCache.get(key) ?? null
}

export function clearCaches() {
  levelCache.clear()
  extremaCache.clear()
}

function yearsBetween(from: number, to: number): number[] {
  const a = new Date(from).getUTCFullYear()
  const b = new Date(to).getUTCFullYear()
  const years = []
  for (let y = a; y <= b; y++) years.push(y)
  return years
}

/** Level points in [from, to], stitched across year boundaries */
export async function levelsBetween(station: string, from: number, to: number): Promise<Point[]> {
  const points: Point[] = []
  for (const year of yearsBetween(from, to)) {
    const s = await loadLevels(station, year)
    if (!s) continue
    const step = s.intervalMinutes * 60_000
    const first = Math.max(0, Math.floor((from - s.start) / step))
    const last = Math.min(s.values.length - 1, Math.ceil((to - s.start) / step))
    for (let i = first; i <= last; i++) {
      const v = s.values[i]
      if (v !== null && v !== undefined) points.push({ time: s.start + i * step, level: v })
    }
  }
  return points
}

export async function extremaBetween(station: string, from: number, to: number): Promise<Extremum[]> {
  const out: Extremum[] = []
  for (const year of yearsBetween(from, to)) {
    const s = await loadExtrema(station, year)
    if (!s) continue
    for (const e of s.extrema) if (e.time >= from && e.time <= to) out.push(e)
  }
  return out
}

/** Linear interpolation between grid points; null outside the data */
export function levelAt(points: Point[], time: number): number | null {
  if (points.length === 0) return null
  let lo = 0
  let hi = points.length - 1
  if (time < points[lo]!.time || time > points[hi]!.time) return null
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1
    if (points[mid]!.time <= time) lo = mid
    else hi = mid
  }
  const a = points[lo]!
  const b = points[hi]!
  if (b.time === a.time) return a.level
  return a.level + ((b.level - a.level) * (time - a.time)) / (b.time - a.time)
}

/** Midnight (local time) of the day containing `time` */
export function startOfLocalDay(time: number): number {
  const d = new Date(time)
  d.setHours(0, 0, 0, 0)
  return d.getTime()
}

export function addDays(time: number, days: number): number {
  const d = new Date(time)
  d.setDate(d.getDate() + days)
  return d.getTime()
}

/** The same points moved in time, for a place whose tide runs later than its station's */
export function shiftPoints(points: Point[], offsetMs: number): Point[] {
  return offsetMs === 0 ? points : points.map(p => ({ time: p.time + offsetMs, level: p.level }))
}
